import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, CornerDownLeft, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCopilot } from './CopilotContext';
import type { CopilotAction } from './copilotActions';

export function CommandPalette() {
  const { paletteOpen, closePalette, actions, runAction, openPanel, sendMessage } = useCopilot();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset on open.
  useEffect(() => {
    if (paletteOpen) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [paletteOpen]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return actions;
    return actions.filter(
      (a) =>
        a.label.toLowerCase().includes(q) ||
        a.hint.toLowerCase().includes(q) ||
        a.keywords.some((k) => k.includes(q)) ||
        a.group.toLowerCase().includes(q)
    );
  }, [query, actions]);

  // "Ask the Copilot" escape hatch when the query isn't a command.
  const askItem = query.trim().length > 0;
  const totalItems = filtered.length + (askItem ? 1 : 0);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const runActive = () => {
    if (askItem && active === filtered.length) {
      const q = query.trim();
      closePalette();
      openPanel();
      sendMessage(q);
      return;
    }
    const action = filtered[active];
    if (action) {
      closePalette();
      runAction(action.id);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, totalItems - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); runActive(); }
  };

  // Keep the active row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // Group the filtered actions while preserving a flat index for keyboard nav.
  let flatIndex = -1;
  const groups = useMemo(() => {
    const map = new Map<string, CopilotAction[]>();
    filtered.forEach((a) => {
      const arr = map.get(a.group) ?? [];
      arr.push(a);
      map.set(a.group, arr);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <AnimatePresence>
      {paletteOpen && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-900/40 px-4 pt-[12vh] backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closePalette}
        >
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search */}
            <div className="flex items-center gap-2.5 border-b border-slate-200 px-4">
              <Search className="h-4 w-4 shrink-0 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search actions or ask the Copilot…"
                className="h-12 flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
              />
              <kbd className="hidden shrink-0 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:block">ESC</kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-2">
              {groups.map(([group, items]) => (
                <div key={group} className="mb-1">
                  <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{group}</p>
                  {items.map((action) => {
                    flatIndex += 1;
                    const idx = flatIndex;
                    const Icon = action.icon;
                    const isActive = idx === active;
                    return (
                      <button
                        key={action.id}
                        data-idx={idx}
                        type="button"
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => { closePalette(); runAction(action.id); }}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                          isActive ? 'bg-primary/10' : 'hover:bg-slate-50'
                        )}
                      >
                        <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', isActive ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500')}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cn('block truncate text-sm font-medium', isActive ? 'text-primary' : 'text-slate-800')}>{action.label}</span>
                          <span className="block truncate text-xs text-muted-foreground">{action.hint}</span>
                        </span>
                        {isActive && <CornerDownLeft className="h-3.5 w-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              ))}

              {askItem && (
                <button
                  data-idx={filtered.length}
                  type="button"
                  onMouseEnter={() => setActive(filtered.length)}
                  onClick={runActive}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                    active === filtered.length ? 'bg-primary/10' : 'hover:bg-slate-50'
                  )}
                >
                  <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', active === filtered.length ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500')}>
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block truncate text-sm font-medium', active === filtered.length ? 'text-primary' : 'text-slate-800')}>
                      Ask Copilot: “{query.trim()}”
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">Get an answer or let it do it for you</span>
                  </span>
                </button>
              )}

              {totalItems === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matching actions.</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
